import { Loader, Music, Upload } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { EmptyState } from "../components/EmptyState";
import { TrackList } from "../components/TrackList";
import { useLocalFilesStore } from "../stores/localFilesStore";
import { useLocalLibraryStore } from "../stores/localLibraryStore";
import { usePlayerStore } from "../stores/playerStore";

export const LocalFiles = () => {
  const { files, addFile, removeFile, cleanup, loadFilesFromDB, initialized } = useLocalFilesStore();
  const { syncLocalFiles, addLocalTrack, removeLocalTrack } = useLocalLibraryStore();
  const { setCurrentTrack, setIsPlaying } = usePlayerStore();
  const [isLoading, setIsLoading] = useState(!initialized);
  const [error, setError] = useState<string | null>(null);

  // Load files when component mounts if not initialized
  useEffect(() => {
    const initializeFiles = async () => {
      if (!initialized) {
        setIsLoading(true);
        try {
          await loadFilesFromDB((loadedFiles) => {
            syncLocalFiles(loadedFiles);
          });
        } catch (err) {
          console.error("Error loading files:", err);
          setError("Failed to load local files");
        } finally {
          setIsLoading(false);
        }
      }
    };

    initializeFiles();
  }, [initialized, loadFilesFromDB, syncLocalFiles]);

  // Cleanup blob URLs when component unmounts
  useEffect(() => {
    return () => cleanup();
  }, [cleanup]);

  const handleFileSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!e.target.files?.length) return;

      setIsLoading(true);
      setError(null);

      try {
        const filePromises = Array.from(e.target.files).map((file) =>
          addFile(file, (track) => {
            addLocalTrack(track);
          })
        );
        await Promise.all(filePromises);
      } catch (err) {
        console.error("Error processing files:", err);
        setError("Failed to process audio files. Please try again.");
      } finally {
        setIsLoading(false);
      }
    },
    [addFile, addLocalTrack]
  );

  const handleRemoveFile = useCallback(
    (id: string) => {
      removeFile(id, (removedId) => {
        removeLocalTrack(removedId);
      });
    },
    [removeFile, removeLocalTrack]
  );

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh]">
        <Loader className="w-8 h-8 animate-spin text-yellow-500" />
        <p className="text-zinc-400 mt-4">Loading local files...</p>
      </div>
    );
  }

  if (error) {
    return (
      <EmptyState
        icon={Music}
        title="Failed to Load Files"
        description={error}
        action={{
          label: "Try Again",
          onClick: () => {
            setError(null);
            loadFilesFromDB((loadedFiles) => {
              syncLocalFiles(loadedFiles);
            });
          },
        }}
      />
    );
  }

  const renderContent = () => {
    if (!files.length) {
      return (
        <div className="p-6">
          <EmptyState
            icon={Music}
            title="No Local Files"
            description="Upload your music files to play them offline"
            action={{
              label: "Upload Files",
              onClick: () => document.getElementById("file-upload")?.click(),
            }}
          />
          <input
            id="file-upload"
            type="file"
            multiple
            accept="audio/*"
            className="hidden"
            onChange={handleFileSelect}
          />
        </div>
      );
    }
    return (
      <>
        <div className="flex justify-between items-center mb-6">
          <label className="px-4 py-2 bg-yellow-500 rounded-full hover:bg-yellow-600 transition cursor-pointer">
            <input type="file" multiple accept="audio/*" className="hidden" onChange={handleFileSelect} />
            <Upload className="w-5 h-5 inline-block mr-2" />
            Upload Files
          </label>
        </div>

        <TrackList
          tracks={files}
          onPlay={(track) => {
            setCurrentTrack(track);
            setIsPlaying(true);
          }}
          onRemove={handleRemoveFile}
          showLocalControls
        />
      </>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-zinc-800/50 to-zinc-900">
      <div className="max-w-7xl mx-auto px-3 md:px-6 py-4 md:py-8">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-4xl font-bold mb-2">Local Files</h1>
          </div>
        </div>

        {renderContent()}
      </div>
    </div>
  );
};
